// ═══════════════════════════════════════
// ATLAS BEASTS — LANDING PAGE
// Dark immersive game landing page
// ═══════════════════════════════════════

import { useNavigate } from 'react-router-dom'

const SEASON_EVENTS = [
  { id: 1, title: 'Season 1 Launch', date: 'May 2026', desc: 'Begin your journey in the Atlas', status: 'active' },
  { id: 2, title: 'Legendary Drop Event', date: 'Coming Soon', desc: 'Increased legendary creature spawn rates', status: 'upcoming' },
  { id: 3, title: 'PVP Tournament', date: 'June 2026', desc: 'Compete for exclusive rewards', status: 'upcoming' },
]

export default function Landing() {
  const navigate = useNavigate()

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#020510' }}>
      
      {/* Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-4 md:px-6 pt-safe-top pb-2 md:pb-4" style={{ 
        background: 'rgba(2,5,16,0.8)', 
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(58,58,90,0.3)',
        paddingTop: 'max(0.5rem, env(safe-area-inset-top))',
      }}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg flex items-center justify-center" style={{ 
            background: 'linear-gradient(135deg, #00F0FF, #BF00FF)',
          }}>
            <span className="font-black text-xs md:text-sm" style={{ color: '#020510' }}>A</span>
          </div>
          <span className="font-bold text-sm md:text-lg uppercase" style={{ color: '#EAF6FF', fontFamily: 'Rajdhani, system-ui' }}>
            Atlas Beasts
          </span>
        </div>
        <button
          onClick={() => navigate('/login')}
          className="px-3 py-1.5 md:px-5 md:py-2.5 rounded-lg font-bold text-xs md:text-sm uppercase tracking-wider transition-all hover:scale-105 hover:shadow-lg"
          style={{
            background: 'linear-gradient(135deg, #00F0FF 0%, #00F0FF80 100%)',
            color: '#020510',
            fontFamily: 'Rajdhani, system-ui',
            boxShadow: '0 0 20px rgba(0,240,255,0.3)',
          }}
        >
          Play Now
        </button>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Animated background */}
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 50% 50%, rgba(0,240,255,0.08) 0%, transparent 60%), radial-gradient(ellipse at 80% 20%, rgba(191,0,255,0.06) 0%, transparent 50%)',
        }} />
        
        {/* Animated particles/dots */}
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.3) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }} />

        {/* Floating game assets */}
        <div className="absolute top-1/4 right-1/4 w-32 h-32 opacity-20 animate-float" style={{ animation: 'float 6s ease-in-out infinite' }}>
          <img src="/assets/kenney/kenney_mini-dungeon/Previews/chest.png" alt="" className="w-full h-full object-contain" />
        </div>
        <div className="absolute bottom-1/3 left-1/4 w-24 h-24 opacity-15" style={{ animation: 'float 8s ease-in-out infinite 1s' }}>
          <img src="/assets/kenney/kenney_mini-dungeon/Previews/coin.png" alt="" className="w-full h-full object-contain" />
        </div>

        <div className="relative z-10 px-4 max-w-5xl mx-auto w-full text-center">
          {/* Small label */}
          <div className="mb-6" style={{ animation: 'fadeInUp 1s ease-out' }}>
            <span className="inline-block px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest" style={{
              color: '#00F0FF',
              background: 'rgba(0,240,255,0.1)',
              border: '1px solid rgba(0,240,255,0.3)',
              fontFamily: 'Space Mono, monospace',
            }}>
              Season 1 — Now Live
            </span>
          </div>

          {/* Main Title */}
          <h1 className="font-black text-7xl md:text-9xl uppercase tracking-tight mb-4" style={{ 
            color: '#EAF6FF',
            fontFamily: 'Rajdhani, system-ui',
            textShadow: '0 0 80px rgba(0,240,255,0.4), 0 0 160px rgba(191,0,255,0.2)',
            animation: 'fadeInUp 1s ease-out 0.1s both',
            lineHeight: 0.9,
          }}>
            ATLAS
            <br />
            <span style={{ 
              background: 'linear-gradient(135deg, #00F0FF 0%, #BF00FF 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>BEASTS</span>
          </h1>
          
          {/* Subtitle */}
          <p className="text-lg md:text-xl mb-10 max-w-2xl mx-auto" style={{ 
            color: '#8A8AA5',
            fontFamily: 'Inter, sans-serif',
            animation: 'fadeInUp 1s ease-out 0.3s both',
          }}>
            Collect elemental creatures, master turn-based combat, and trade your way to the top in this creature collection RPG.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12" style={{ animation: 'fadeInUp 1s ease-out 0.5s both' }}>
            <button
              onClick={() => navigate('/login')}
              className="px-12 py-5 rounded-xl font-black text-xl uppercase tracking-wider transition-all hover:scale-105 active:scale-95 relative overflow-hidden group"
              style={{
                background: 'linear-gradient(135deg, #00F0FF 0%, #00F0FF80 100%)',
                color: '#020510',
                fontFamily: 'Rajdhani, system-ui',
                boxShadow: '0 0 40px rgba(0,240,255,0.4), 0 0 80px rgba(0,240,255,0.2)',
                minWidth: '240px',
              }}
            >
              <span className="relative z-10">Play Free</span>
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, transparent 100%)',
              }} />
            </button>
            <button
              onClick={() => navigate('/login')}
              className="px-12 py-5 rounded-xl font-black text-xl uppercase tracking-wider transition-all hover:scale-105 active:scale-95 relative overflow-hidden group"
              style={{
                background: 'transparent',
                color: '#EAF6FF',
                fontFamily: 'Rajdhani, system-ui',
                border: '2px solid rgba(0,240,255,0.5)',
                boxShadow: '0 0 20px rgba(0,240,255,0.1)',
                minWidth: '240px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#00F0FF'
                e.currentTarget.style.boxShadow = '0 0 30px rgba(0,240,255,0.3)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(0,240,255,0.5)'
                e.currentTarget.style.boxShadow = '0 0 20px rgba(0,240,255,0.1)'
              }}
            >
              Season Info
            </button>
          </div>

          {/* Stats row */}
          <div className="flex justify-center gap-8 md:gap-16" style={{ animation: 'fadeInUp 1s ease-out 0.7s both' }}>
            {[
              { value: '50+', label: 'Creatures' },
              { value: '6', label: 'Elements' },
              { value: '∞', label: 'Battles' },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <p className="font-black text-3xl md:text-4xl" style={{ 
                  color: '#EAF6FF', 
                  fontFamily: 'Rajdhani, system-ui',
                  textShadow: '0 0 20px rgba(0,240,255,0.3)',
                }}>
                  {stat.value}
                </p>
                <p className="text-xs uppercase tracking-wider" style={{ color: '#5A5A7A', fontFamily: 'Space Mono, monospace' }}>
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2" style={{ animation: 'bounce 2s infinite' }}>
          <div className="w-6 h-10 rounded-full border-2 flex items-start justify-center p-1" style={{ borderColor: 'rgba(255,255,255,0.2)' }}>
            <div className="w-1 h-2 rounded-full" style={{ background: '#00F0FF', animation: 'scrollDown 2s infinite' }} />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4" style={{ background: 'rgba(13,13,21,0.5)' }}>
        <div className="max-w-6xl mx-auto">
          <h2 className="font-black text-4xl md:text-5xl uppercase text-center mb-4" style={{ 
            color: '#EAF6FF',
            fontFamily: 'Rajdhani, system-ui',
          }}>
            GAME FEATURES
          </h2>
          <p className="text-center mb-12" style={{ color: '#5A5A7A', fontFamily: 'Inter, sans-serif' }}>
            Everything you need to know about the game
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { 
                icon: '/assets/kenney/kenney_mini-arena/Previews/banner.png', 
                title: 'Hunt Bounties', 
                desc: 'Track down legendary creatures across the atlas. Complete daily and weekly bounties to earn rare rewards.' 
              },
              { 
                icon: '/assets/kenney/kenney_mini-dungeon/Previews/weapon-sword.png', 
                title: 'Turn-Based Combat', 
                desc: 'Strategic battles with elemental matchups. Fire beats earth, water beats fire — master the elements to win.' 
              },
              { 
                icon: '/assets/kenney/kenney_mini-dungeon/Previews/coin.png', 
                title: 'Trade Marketplace', 
                desc: 'Buy, sell, and trade with players worldwide. Build your collection through smart trading.' 
              },
            ].map((feature, i) => (
              <div key={i} className="group p-6 rounded-2xl border transition-all hover:scale-105" style={{ 
                background: 'rgba(13,13,21,0.8)',
                borderColor: 'rgba(58,58,90,0.5)',
                backdropFilter: 'blur(8px)',
              }}>
                <div className="w-16 h-16 rounded-xl mb-4 flex items-center justify-center" style={{
                  background: 'linear-gradient(135deg, rgba(0,240,255,0.1), rgba(191,0,255,0.1))',
                  border: '1px solid rgba(0,240,255,0.2)',
                }}>
                  <img src={feature.icon} alt={feature.title} className="w-10 h-10 object-contain" />
                </div>
                <h3 className="font-bold text-xl mb-2" style={{ color: '#EAF6FF', fontFamily: 'Rajdhani, system-ui' }}>
                  {feature.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: '#8A8AA5', fontFamily: 'Inter, sans-serif' }}>
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Screenshots/Media Showcase */}
      <section className="py-20 px-4" style={{ background: 'rgba(13,13,21,0.5)' }}>
        <div className="max-w-6xl mx-auto">
          <h2 className="font-black text-4xl md:text-5xl uppercase text-center mb-4" style={{ 
            color: '#EAF6FF',
            fontFamily: 'Rajdhani, system-ui',
          }}>
            SCREENSHOTS
          </h2>
          <p className="text-center mb-12" style={{ color: '#5A5A7A', fontFamily: 'Inter, sans-serif' }}>
            See the Atlas Beasts in action
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: 'World Map', desc: 'Explore the vast atlas', icon: '/assets/kenney/kenney_mini-arena/Previews/banner.png', color: '#00F0FF' },
              { title: 'Battle Arena', desc: 'Turn-based combat', icon: '/assets/kenney/kenney_mini-dungeon/Previews/weapon-sword.png', color: '#FF4800' },
              { title: 'Marketplace', desc: 'Trade with players', icon: '/assets/kenney/kenney_mini-dungeon/Previews/coin.png', color: '#39FF14' },
              { title: 'Creature Vault', desc: 'Manage your collection', color: '#BF00FF' },
              { title: 'Bounty Board', desc: 'Hunt legendary beasts', icon: '/assets/kenney/kenney_mini-dungeon/Previews/chest.png', color: '#FFE800' },
              { title: 'Character Roster', desc: 'Build your crew', color: '#00F0FF' },
            ].map((screenshot, i) => (
              <div key={i} className="group rounded-2xl overflow-hidden border transition-all hover:scale-105" style={{ 
                background: 'rgba(13,13,21,0.8)',
                borderColor: 'rgba(58,58,90,0.5)',
                aspectRatio: '16/9',
              }}>
                <div className="w-full h-full flex flex-col items-center justify-center p-6" style={{
                  background: `linear-gradient(135deg, ${screenshot.color}10, rgba(13,13,21,0.8))`,
                }}>
                  {screenshot.icon ? (
                    <img src={screenshot.icon} alt={screenshot.title} className="w-20 h-20 object-contain mb-4" />
                  ) : (
                    <div className="w-20 h-20 rounded-xl flex items-center justify-center mb-4" style={{
                      background: `${screenshot.color}20`,
                      border: `1px solid ${screenshot.color}40`,
                    }}>
                      <div className="w-10 h-10 rounded" style={{ background: screenshot.color }} />
                    </div>
                  )}
                  <h3 className="font-bold text-lg mb-1" style={{ color: '#EAF6FF', fontFamily: 'Rajdhani, system-ui' }}>
                    {screenshot.title}
                  </h3>
                  <p className="text-sm text-center" style={{ color: '#8A8AA5', fontFamily: 'Inter, sans-serif' }}>
                    {screenshot.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Season Events */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-black text-4xl md:text-5xl uppercase text-center mb-4" style={{ 
            color: '#EAF6FF',
            fontFamily: 'Rajdhani, system-ui',
          }}>
            SEASON EVENTS
          </h2>
          <p className="text-center mb-12" style={{ color: '#5A5A7A', fontFamily: 'Inter, sans-serif' }}>
            Current and upcoming events
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {SEASON_EVENTS.map((event) => (
              <div key={event.id} className="p-6 rounded-2xl border transition-all hover:border-opacity-100" style={{ 
                background: 'rgba(13,13,21,0.8)',
                borderColor: event.status === 'active' ? 'rgba(0,240,255,0.5)' : 'rgba(58,58,90,0.5)',
                backdropFilter: 'blur(8px)',
              }}>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-[10px] font-bold uppercase px-3 py-1 rounded-full" style={{ 
                    color: event.status === 'active' ? '#020510' : '#8A8AA5',
                    background: event.status === 'active' ? '#00F0FF' : 'rgba(58,58,90,0.3)',
                    fontFamily: 'Space Mono, monospace',
                  }}>
                    {event.status}
                  </span>
                  <span className="text-xs" style={{ color: '#5A5A7A', fontFamily: 'Space Mono, monospace' }}>
                    {event.date}
                  </span>
                </div>
                <h3 className="font-bold text-xl mb-2" style={{ color: '#EAF6FF', fontFamily: 'Rajdhani, system-ui' }}>
                  {event.title}
                </h3>
                <p className="text-sm" style={{ color: '#8A8AA5', fontFamily: 'Inter, sans-serif' }}>
                  {event.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof / Community */}
      <section className="py-20 px-4" style={{ background: 'rgba(13,13,21,0.3)' }}>
        <div className="max-w-6xl mx-auto">
          <h2 className="font-black text-4xl md:text-5xl uppercase text-center mb-4" style={{ 
            color: '#EAF6FF',
            fontFamily: 'Rajdhani, system-ui',
          }}>
            JOIN THE COMMUNITY
          </h2>
          <p className="text-center mb-12" style={{ color: '#5A5A7A', fontFamily: 'Inter, sans-serif' }}>
            Connect with thousands of beast hunters worldwide
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {[
              { icon: '/assets/kenney/kenney_animated-retro/Previews/retroChar_0.png', title: '10K+ Players', desc: 'Active daily' },
              { icon: '/assets/kenney/kenney_mini-dungeon/Previews/weapon-sword.png', title: '1M+ Battles', desc: 'Fought so far' },
              { icon: '/assets/kenney/kenney_mini-dungeon/Previews/chest.png', title: '500+ Tournaments', desc: 'Completed' },
            ].map((stat, i) => (
              <div key={i} className="text-center p-6 rounded-2xl border" style={{ 
                background: 'rgba(13,13,21,0.8)',
                borderColor: 'rgba(58,58,90,0.5)',
              }}>
                <img src={stat.icon} alt={stat.title} className="w-16 h-16 object-contain mx-auto mb-3" />
                <h3 className="font-black text-3xl mb-1" style={{ color: '#EAF6FF', fontFamily: 'Rajdhani, system-ui' }}>
                  {stat.title}
                </h3>
                <p className="text-sm" style={{ color: '#8A8AA5', fontFamily: 'Inter, sans-serif' }}>
                  {stat.desc}
                </p>
              </div>
            ))}
          </div>

          {/* Platform Info */}
          <div className="text-center p-8 rounded-2xl border" style={{ 
            background: 'rgba(13,13,21,0.8)',
            borderColor: 'rgba(58,58,90,0.5)',
          }}>
            <h3 className="font-bold text-xl mb-6" style={{ color: '#EAF6FF', fontFamily: 'Rajdhani, system-ui' }}>
              PLAY ANYWHERE
            </h3>
            <div className="flex flex-wrap justify-center gap-4">
              {[
                { label: 'Mobile', color: '#00F0FF' },
                { label: 'Desktop', color: '#BF00FF' },
                { label: 'Tablet', color: '#FFE800' },
                { label: 'Web Browser', color: '#39FF14' },
              ].map((platform, i) => (
                <div key={i} className="flex flex-col items-center gap-2 px-6 py-4 rounded-xl" style={{
                  background: 'rgba(58,58,90,0.5)',
                  border: `1px solid ${platform.color}40`,
                  minWidth: '120px',
                }}>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{
                    background: `${platform.color}20`,
                    border: `1px solid ${platform.color}40`,
                  }}>
                    <div className="w-5 h-5 rounded" style={{ background: platform.color }} />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-wider" style={{ 
                    color: '#EAF6FF',
                    fontFamily: 'Rajdhani, system-ui',
                  }}>
                    {platform.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4" style={{ 
        background: 'radial-gradient(ellipse at center, rgba(0,240,255,0.05) 0%, transparent 70%)',
      }}>
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-black text-4xl md:text-6xl uppercase mb-4" style={{ 
            color: '#EAF6FF',
            fontFamily: 'Rajdhani, system-ui',
          }}>
            READY TO START?
          </h2>
          <p className="mb-8" style={{ color: '#8A8AA5', fontFamily: 'Inter, sans-serif' }}>
            Join thousands of players already hunting beasts in the Atlas.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="px-16 py-6 rounded-xl font-black text-2xl uppercase tracking-wider transition-all hover:scale-105 active:scale-95 relative overflow-hidden group"
            style={{
              background: 'linear-gradient(135deg, #00F0FF 0%, #BF00FF 100%)',
              color: '#020510',
              fontFamily: 'Rajdhani, system-ui',
              boxShadow: '0 0 60px rgba(0,240,255,0.4), 0 0 120px rgba(191,0,255,0.2)',
            }}
          >
            <span className="relative z-10">Play Free Now</span>
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.3) 0%, transparent 100%)',
            }} />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t" style={{ borderColor: 'rgba(58,58,90,0.3)' }}>
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded" style={{ 
              background: 'linear-gradient(135deg, #00F0FF, #BF00FF)',
            }} />
            <span className="font-bold text-sm" style={{ color: '#8A8AA5', fontFamily: 'Rajdhani, system-ui' }}>
              Atlas Beasts
            </span>
          </div>
          <p className="text-xs" style={{ color: '#5A5A7A', fontFamily: 'Space Mono, monospace' }}>
            © 2026 Atlas Beasts. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Animations */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% {
            transform: translateX(-50%) translateY(0);
          }
          40% {
            transform: translateX(-50%) translateY(-10px);
          }
          60% {
            transform: translateX(-50%) translateY(-5px);
          }
        }
        @keyframes scrollDown {
          0% {
            opacity: 1;
            transform: translateY(0);
          }
          100% {
            opacity: 0;
            transform: translateY(8px);
          }
        }
      `}</style>
    </div>
  )
}
